import React, { useState } from 'react'
import {
  AppBar,
  Toolbar,
  Typography,
  Container,
  Box,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material'
import { Menu as MenuIcon, Close as CloseIcon } from '@mui/icons-material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import config from '../config'

const Header = ({ companyName, companyAvatar }) => {
  const navigate = useNavigate()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Get the best available image URL and make it absolute if needed
  const getImageUrl = url => {
    if (!url) return null
    // If URL starts with /, prepend server URL
    if (url.startsWith('/')) {
      return `${config.server}${url}`
    }
    return url
  }

  // Prefer the original image — image_medium/image_thumbnail are re-encoded as
  // JPEG by the backend, which strips transparency from PNG logos.
  const avatarUrl =
    getImageUrl(companyAvatar?.image) ||
    getImageUrl(companyAvatar?.imageMedium) ||
    getImageUrl(companyAvatar?.imageThumbnail)

  const menuItems = [
    { label: 'Services', path: '/services' },
    { label: 'Reviews', path: '/reviews' },
    { label: 'FAQ', path: '/faq' },
  ]

  const handleMenuClick = path => {
    navigate(path)
    setMobileMenuOpen(false)
  }

  return (
    <>
      <AppBar position='sticky' elevation={0} sx={{ bgcolor: '#201e31' }}>
        <Container maxWidth='lg'>
          <Toolbar disableGutters>
            <Box
              component={RouterLink}
              to='/'
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                flexGrow: 1,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              {avatarUrl ? (
                <Box
                  component='img'
                  src={avatarUrl}
                  alt={companyName}
                  sx={{
                    height: 40,
                    maxWidth: { xs: 150, sm: 200, md: 280 },
                    objectFit: 'contain',
                    backgroundColor: 'transparent',
                  }}
                />
              ) : (
                <Typography
                  variant='h6'
                  sx={{ fontWeight: 600, color: 'inherit' }}
                >
                  {companyName}
                </Typography>
              )}
            </Box>

            {/* Desktop Menu */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 3 }}>
              {menuItems.map(item => (
                <Typography
                  key={item.path}
                  component={RouterLink}
                  to={item.path}
                  sx={{
                    color: 'inherit',
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {item.label}
                </Typography>
              ))}
            </Box>

            {/* Mobile Menu Icon */}
            <IconButton
              color='inherit'
              edge='end'
              onClick={() => setMobileMenuOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mobile Drawer Menu */}
      <Drawer
        anchor='right'
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <Box sx={{ width: 250 }} role='presentation'>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 2,
            }}
          >
            <Typography variant='h6' sx={{ fontWeight: 600 }}>
              Menu
            </Typography>
            <IconButton onClick={() => setMobileMenuOpen(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Divider />
          <List>
            {menuItems.map(item => (
              <ListItem key={item.path} disablePadding>
                <ListItemButton onClick={() => handleMenuClick(item.path)}>
                  <ListItemText primary={item.label} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </>
  )
}

export default Header
